import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Snackbar,
  Alert,
  Chip,
  Card,
  CardContent,
  CardActions,
  Grid,
  useMediaQuery,
  useTheme,
  Tooltip,
} from '@mui/material';
import { formatCurrency, formatDuration } from '../utils/formatters';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccessTime as TimeIcon,
  AttachMoney as MoneyIcon,
  EventAvailable as EventAvailableIcon,
} from '@mui/icons-material';
import { useServices } from '../hooks/queries';
import { useThemeCustom } from '../hooks/useThemeCustom';
import { useContextualHint } from '../hooks/useContextualHint';
import useDialog from '../hooks/useDialog';
import useSnackbar from '../hooks/useSnackbar';
import { useLocation } from 'react-router-dom';
import LoadingState from '../components/ui/LoadingState';
import EmptyState from '../components/ui/EmptyState';
import ErrorState from '../components/ui/ErrorState';
import CascadeDeleteDialog from '../components/ui/CascadeDeleteDialog';
import ContextualHint from '../components/ContextualHint';
import ServiceForm from '../components/forms/ServiceForm';
import AppointmentFormDialog from '../components/AppointmentFormDialog';

import professionalsService from '../services/professionalsService';
import placesService from '../services/placesService';

import SmartWizard from '../components/wizard/SmartWizard';
import { tableContainer, tableHeadCell, pageHeader } from '../styles/pageStyles';
import { getIconClass } from '../utils/iconUtils';

export default function ServicesPage() {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { getLabel, getIcon } = useThemeCustom();
  
  // ✅ React Query Hooks
  const { items: services, loading, error, reload, create, update, deleteItem } = useServices();
  const formDialog = useDialog();
  const deleteDialog = useDialog();
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  // Estados para diálogo de agendamento
  const [showSmartWizard, setShowSmartWizard] = useState(false);
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [appointmentPrefillConfig, setAppointmentPrefillConfig] = useState(null);
  const [professionals, setProfessionals] = useState([]);
  const [places, setPlaces] = useState([]);

  // Obter dica contextual
  const hint = useContextualHint('services', { items: services || [] });

  // Abrir criação via querystring (?create=1)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('create') === '1') {
      setShowSmartWizard(true);
      const cleanUrl = location.pathname;
      window.history.replaceState(null, '', cleanUrl);
    }
  }, [location.pathname, location.search]);

  // Carregar dados para o diálogo de agendamento
  useEffect(() => {
    const loadData = async () => {
      try {
        const [profsData, placesData] = await Promise.all([
          professionalsService.getAll(),
          placesService.getAll()
        ]);
        setProfessionals(profsData || []);
        setPlaces(placesData || []);
      } catch (err) {
        console.warn('[ServicesPage] Erro ao carregar dados para agendamento:', err);
      }
    };
    loadData();
  }, []);

  // ✅ Handlers
  const handleCreate = async (data) => {
    try {
      await create(data);
      formDialog.handleClose();
      showSuccess('Serviço criado com sucesso!');
    } catch (err) {
      showError('Erro ao criar serviço: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleUpdate = async (data) => {
    try {
      // ✅ useQueryCRUD.update espera { id, data }
      await update({ id: formDialog.data.id, data });
      formDialog.handleClose();
      showSuccess('Serviço atualizado com sucesso!');
    } catch (err) {
      showError('Erro ao atualizar serviço: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSubmit = formDialog.isEdit ? handleUpdate : handleCreate;

  // ✅ Handlers de Agendamento
  const handleOpenAppointment = useCallback((service) => {
    setAppointmentPrefillConfig({
      serviceId: service.id,
    });
    setAppointmentDialogOpen(true);
  }, []);

  const handleCloseAppointmentDialog = useCallback(() => {
    setAppointmentDialogOpen(false);
    setAppointmentPrefillConfig(null);
  }, []);

  const handleAppointmentSuccess = useCallback((appointment) => {
    showSuccess('Agendamento criado com sucesso!');
    setAppointmentDialogOpen(false);
    setAppointmentPrefillConfig(null);
  }, [showSuccess]);



  // ✅ Loading/Error States
  if (loading) return <LoadingState message="Carregando serviços..." />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <Box>
      {/* Header */}
      <Box sx={pageHeader}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight="bold" color="primary.main">
            {React.createElement(getIconClass(getIcon('services').name), { sx: { mr: 1, verticalAlign: 'middle' } })}
            {getLabel('services')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getLabel('servicesDescription')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowSmartWizard(true)}
          >
            Novo Serviço
          </Button>
        </Box>
      </Box>

      {/* Dica Contextual */}
      <ContextualHint hint={hint} onActionClick={() => setShowSmartWizard(true)} />

      {/* Empty State */}
      {services.length === 0 ? (
        <EmptyState
          icon={getIconClass(getIcon('services').name)}
          title="Nenhum serviço cadastrado"
          message="Clique em 'Novo Serviço' para começar."
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowSmartWizard(true)}
            >
              Novo Serviço
            </Button>
          }
        />
      ) : isMobile ? (
        // ✅ MOBILE: Cards Grid
        <Grid container spacing={2}>
          {services.map((service) => (
            <Grid item xs={12} key={service.id}>
              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderLeft: '3px solid', borderLeftColor: 'primary.main' }}>
                <CardContent sx={{ pb: 1 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {service.name}
                    </Typography>
                    {service.description && (
                      <Typography variant="body2" color="text.secondary">
                        {service.description}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ mb: 1.5 }}>
                    <Chip 
                      label={service.category} 
                      size="small" 
                      color="primary"
                      variant="outlined"
                    />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TimeIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {formatDuration(service.defaultDurationMinutes)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <MoneyIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {formatCurrency(service.defaultPrice)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Tooltip title="Agendar este serviço">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenAppointment(service)}
                      color="success"
                    >
                      <EventAvailableIcon />
                    </IconButton>
                  </Tooltip>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => formDialog.handleOpen(service)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => deleteDialog.handleOpen(service)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        // ✅ DESKTOP: Table
        <TableContainer component={Paper} variant="outlined" sx={tableContainer}>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell sx={tableHeadCell}>Nome</TableCell>
                <TableCell sx={tableHeadCell}>Categoria</TableCell>
                <TableCell sx={tableHeadCell}>Duração Padrão</TableCell>
                <TableCell sx={tableHeadCell}>Preço Padrão</TableCell>
                <TableCell align="right" sx={tableHeadCell}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {services.map((service) => (
                <TableRow key={service.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {service.name}
                      </Typography>
                      {service.description && (
                        <Typography variant="body2" color="text.secondary">
                          {service.description}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={service.category} size="small" />
                  </TableCell>
                  <TableCell>{formatDuration(service.defaultDurationMinutes)}</TableCell>
                  <TableCell>{formatCurrency(service.defaultPrice)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Agendar este serviço">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenAppointment(service)}
                        color="success"
                      >
                        <EventAvailableIcon />
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={() => formDialog.handleOpen(service)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => deleteDialog.handleOpen(service)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Service Form Dialog */}
      <ServiceForm
        open={formDialog.open}
        onClose={formDialog.handleClose}
        onSubmit={handleSubmit}
        data={formDialog.data}
      />

      {/* Delete Confirmation Dialog */}
      <CascadeDeleteDialog
        open={deleteDialog.open}
        onClose={deleteDialog.handleClose}
        onDeleted={() => {
          deleteDialog.handleClose();
          showSuccess('Excluído com sucesso!');
          reload();
        }}
        deletePath={`/services/${deleteDialog.data?.id}`}
        entityName={deleteDialog.data?.name}
      />

      {/* Diálogo de Agendamento - auto-suficiente, carrega próprios dados */}
      <AppointmentFormDialog
        open={appointmentDialogOpen}
        onClose={handleCloseAppointmentDialog}
        onSuccess={handleAppointmentSuccess}
        prefillConfig={appointmentPrefillConfig}
      />

      {/* SmartWizard */}
      <SmartWizard
        open={showSmartWizard}
        wizardId="add-service"
        onClose={() => setShowSmartWizard(false)}
        onSuccess={() => {
          setShowSmartWizard(false);
          reload();
          showSuccess('Serviço criado com sucesso!');
        }}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
